import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { capitalizeFirstLetters } from '../../utils'
import { presets } from './productPresets'
import Sheet from '../../components/Sheet'
import FormPage from '../../components/FormPage'
import ActionBar from '../../components/ActionBar'
import { useBlockingSpan } from '../../loading/BlockingLoad'
import { styled } from '@mui/material/styles'
import Autocomplete from '@mui/material/Autocomplete'
import TextField from '@mui/material/TextField'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import Grid from '@mui/material/Grid'
import InputAdornment from '@mui/material/InputAdornment'
import ArrowDropDownCircleIcon from '@mui/icons-material/ArrowDropDownCircle'
import CameraAltIcon from '@mui/icons-material/CameraAlt'
import DeleteIcon from '@mui/icons-material/Delete'
import FormControl from '@mui/material/FormControl'
import FormGroup from '@mui/material/FormGroup'
import FormControlLabel from '@mui/material/FormControlLabel'
import Checkbox from '@mui/material/Checkbox'
import Select from '@mui/material/Select'
import InputLabel from '@mui/material/InputLabel'
import OutlinedInput from '@mui/material/OutlinedInput'
import MenuItem from '@mui/material/MenuItem'
import ListItemText from '@mui/material/ListItemText'

const { categories, metals, gemstones, colors } = presets

// Two columns of inputs at a comfortable width. FormPage holds the heading and
// the action row to the same line, so they stay attached to the form.
const FORM_MEASURE = 536

const defaultFields = {
    id: '',
    title: '',
    body_html: '',
    category: '',
    price: '',
    cost: '',
    nwk_quantity: 0,
    lb_quantity: 0,
    vendor: '',
    weight: '',
    image: null
}

const defaultMetafields = {
    name: { value: '', id: '' },
    metal: { value: '', id: '' },
    center_stone: { value: '', id: '' },
    side_stones: { value: '', id: '' },
    gemstones: { value: [], id: '' },
    watch_model: { value: '', id: '' },
    band_width: { value: '', id: '' },
    color: { value: [], id: '' },
    length: { value: { value: '', unit: '' }, id: '' },
    ring_size: { value: '', id: '' }
}

export default function CreateProduct(props) {
    const {
        showCreateProduct,
        setShowCreateProduct,
        setTargetBarcodes,
        fetchResource,
        refreshProducts,
        edit,
        product,
        manageProductTitle,
        vendors
    } = props

    const { t } = useTranslation()
    const runBlocking = useBlockingSpan()
    const [fields, setFields] = useState(defaultFields)
    const [metafields, setMetafields] = useState(defaultMetafields)
    const [imageURLs, setImageURLs] = useState([])
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false)
    const [hasStoneSizes, setHasStoneSizes] = useState(false)
    const [hasDescription, setHasDescription] = useState(false)

    const categoryIsWatch = fields.category.toLowerCase().includes('watch')
    const categoryIsRing =
        fields.category.toLocaleLowerCase() === 'wedding bands' ||
        (
            fields.category.toLowerCase().includes('ring') &&
            !fields.category.toLowerCase().includes('earrings')
        )
    const categoryIsWeddingBand = fields.category === 'Wedding Bands'
    const categoryNeedsLength = [
        'Chains',
        'Bracelets',
        "Men's Bracelets",
        'Baby Bracelets',
        'Charm Necklace Set',
        'Choker',
        'Anklets',
        'Necklaces'
    ].includes(fields.category)

    // Seiko has its own intake screen (AddSeikoWatch), which is the only way a Seiko is
    // created: it derives the title, category and every spec from the model number. Leaving
    // Seiko in this list would give the same watch two doors with different outcomes. An
    // existing Seiko product keeps the option, or editing one would silently blank its
    // vendor -- the Autocomplete drops a value that is not in `options`. The comparison is
    // case-insensitive on both sides: `vendor` is a raw Shopify string that can be edited
    // outside this form (Shopify Admin, CSV import), so an exact match here would silently
    // blank a product whose vendor happens to be stored as `seiko` or `SEIKO`.
    const vendorOptions = String(fields.vendor ?? '').toLowerCase() === 'seiko'
        ? vendors
        : vendors.filter(vendor => vendor.toLowerCase() !== 'seiko')

    // Generate list of gemstones to populate form
    const gemstonesList = Object.keys(gemstones).map(gemstone => {
        return gemstone
            .split('_')
            .map(word => {
                return word.charAt(0).toUpperCase() + word.slice(1)
            })
            .join(' ')
    })

    // Generate list of metals to populate form
    const metalsList = Object.keys(metals).map(metal => metal)
    const colorList = colors.map(color => color)

    const Input = styled('input')({
        display: 'none'
    })

    function handleMetafieldChange(e, optionsValue = null) {
        const { id, value } = e.target

        if (optionsValue) {
            setMetafields(prevFields => ({
                ...prevFields,
                [id.split('-')[0]]: { ...prevFields[id.split('-')[0]], value: optionsValue }
            }))
            return
        }

        setMetafields(prevFields => ({
            ...prevFields,
            [id]: { ...prevFields[id], value: value }
        }))
    }

    function handleChange(e, optionsValue = null) {
        const { id, value } = e.target

        if (optionsValue) {
            setFields(prevFields => ({
                ...prevFields,
                [id.split('-')[0]]: optionsValue
            }))
            return
        }

        setFields(prevFields => ({
            ...prevFields,
            [id]: value
        }))
    }

    async function handleImageSelect(e) {
        const reader = new FileReader()
        const currentImageURLs = imageURLs

        reader.readAsDataURL(e.target.files[0])

        reader.onload = () => {
            const imgElement = document.createElement('img')
            imgElement.src = reader.result

            imgElement.onload = e => {
                const canvas = document.createElement('canvas')
                const maxWidth = 1200
                const scaleSize = maxWidth / e.target.width

                canvas.width = maxWidth
                canvas.height = e.target.height * scaleSize

                const ctx = canvas.getContext('2d')

                ctx.drawImage(e.target, 0, 0, canvas.width, canvas.height)

                const srcEncoded = ctx.canvas.toDataURL('image/jpeg', 0.75)

                setFields(prevFields => ({
                    ...prevFields,
                    image: srcEncoded
                }))
            }
        }

        currentImageURLs.push(URL.createObjectURL(e.target.files[0]))
        setImageURLs(currentImageURLs)
    }

    function handleImageDelete() {
        setFields(prevFields => ({
            ...prevFields,
            image: null
        }))

        URL.revokeObjectURL(imageURLs[0])
        setImageURLs([])
        setShowDeleteConfirmation(false)
    }

    async function handleSubmit(e) {
        e.preventDefault();

        if (parseFloat(fields.cost) >= parseFloat(fields.price)) return alert(t('products.costExceedsPriceError', { cost: parseFloat(fields.cost), price: parseFloat(fields.price) }))

        // One span over both requests. Saving is a single action to the user, and
        // marking the write and the refetch blocking separately would let the overlay
        // fall away in the gap between them and come straight back.
        await runBlocking(async () => {
            const response = await fetchResource({
                resource: `products/${edit ? 'edit' : 'create'}`,
                body: { fields, metafields }
            })
            if (response.result !== "OK") return alert(t('products.editProductError'))

            setShowCreateProduct(false)
            // Was window.location.reload — a full SPA reboot to update one list.
            await refreshProducts()
        })
    }

    function handleCancel() {
        setFields(defaultFields)
        setMetafields(defaultMetafields)
        setImageURLs([])
        setTargetBarcodes([])
        setShowCreateProduct(false)
    }

    // Generate title
    useEffect(() => {
        const metal = metafields.metal.value
        const gemstonesList = metafields.gemstones.value
        let category = fields.category
        let color
        let material
        let title
        let gemstones = ''

        switch (metafields.color.value.length) {
            case 1:
                color = metafields.color.value[0]
                break
            case 2:
                color = 'Two-Tone'
                break
            case 3:
                color = 'Tri-Tone'
                break
        }

        if (color && metal) {
            if (metal.toLowerCase().includes('k')) {
                material = `${metal} ${color || ''} Gold`
            } else {
                material = `${color} ${metal}`
            }
        }

        if (category.toLocaleLowerCase().includes('watches')) {
            category = category.slice(0, -2)
        } else {
            if (category.slice(-1) === 's') {
                category = category.slice(0, -1)
            }
        }

        if (gemstonesList.length) {
            for (let i = 0; i < gemstonesList.length; i++) {
                const stone = gemstonesList[i] === 'Cubic Zirconia' ? 'CZ' : gemstonesList[i]

                if (gemstonesList.length > 1) {
                    if (i === gemstonesList.length - 1) {
                        gemstones += ` and ${stone}`
                        break
                    } else if (i > 0) {
                        gemstones += ', '
                    }
                }
                gemstones += stone
            }
        }

        title = `${material || metal || ''} ${capitalizeFirstLetters(metafields.name.value) || ''} ${
            category || ''
        } ${gemstones ? `with ${gemstones}` : ''}`
        setFields(prevFields => ({
            ...prevFields,
            // An empty part (no name, no metal) would otherwise leave a double space.
            title: title.replace(/\s+/g, ' ').trim()
        }))
    }, [
        metafields.name.value,
        fields.category,
        metafields.metal.value,
        metafields.color.value,
        metafields.gemstones.value
    ])

    useEffect(() => {
        if (!categoryIsWatch)
            setMetafields(prevFields => ({
                ...prevFields,
                watch_model: { ...prevFields.watch_model, value: '' }
            }))
        if (!categoryIsWeddingBand)
            setMetafields(prevFields => ({
                ...prevFields,
                band_width: { ...prevFields.band_width, value: '' }
            }))
    }, [categoryIsWatch])

    useEffect(() => {
        if (metafields.gemstones.value.length === 0) {
            setMetafields(prevFields => ({
                ...prevFields,
                center_stone: { ...prevFields.center_stone, value: '' },
                side_stones: { ...prevFields.side_stones, value: '' }
            }))
            setHasStoneSizes(false)
        }
    }, [metafields.gemstones.value])

    useEffect(() => {
        if (!hasStoneSizes) {
            setMetafields(prevFields => ({
                ...prevFields,
                center_stone: { ...prevFields.center_stone, value: '' },
                side_stones: { ...prevFields.side_stones, value: '' }
            }))
        }
    }, [hasStoneSizes])

    useEffect(() => {
        if (!hasDescription) {
            setFields(prevFields => ({
                ...prevFields,
                body_html: ''
            }))
        }
    }, [hasDescription])

    useEffect(() => {
        if (categoryNeedsLength) {
            setFields(prevFields => ({
                ...prevFields,
                length: ''
            }))
        }
    }, [categoryNeedsLength])

    useEffect(() => {
        if (edit) {
            // Merged over the defaults rather than replacing them. A caller that
            // leaves a field out would otherwise set it to undefined, and React
            // quietly stops managing an input whose value goes undefined -- the
            // field then shows empty over a value the product really has, and any
            // guard reading it compares against NaN.
            setFields({ ...defaultFields, ...product.fields })
            setMetafields(prevFields => ({ ...prevFields, ...product.metafields }))
            setHasDescription(!!product.fields.body_html);
            setHasStoneSizes(!!(product.metafields.center_stone?.value || product.metafields.side_stones?.value));
        } else {
            setFields(defaultFields)
            setMetafields(defaultMetafields)
        }
    }, [edit, product])

    useEffect(() => {
        if (!showCreateProduct) {
            setFields(defaultFields)
            setMetafields(defaultMetafields)
            setImageURLs([])
            setTargetBarcodes([])
        }
    }, [showCreateProduct])

    if (!showCreateProduct) return

    return (
        // A screen of its own rather than a popup. This form runs to 1241px of
        // scroll, 574px of it below the fold on a phone, and it was sharing a
        // container with the two-button confirmations -- one that had no
        // max-height below 600px, so Save sat off the bottom of the screen with
        // nothing to scroll. Products keeps this mounted in place of the list, so
        // page, cursors and selection all survive the round trip.
        <FormPage
            title={manageProductTitle}
            // The generated title, live. It belongs at the top with the heading,
            // not centered in the middle of the fields it is derived from. Null
            // rather than a dash: on a new product there is nothing to generate
            // from yet, and a lone '-' under the heading is a label for nothing.
            subtitle={fields.title || null}
            onSubmit={handleSubmit}
            measure={FORM_MEASURE}
            actions={
                /* The pair used to be split xs={4} / xs={8}, so the safe action got
                   a third of the row and the destructive-looking green one got two
                   thirds. Equal widths, and the colours the right way round. */
                <ActionBar
                    cancel={{ label: t('common.cancel'), onClick: handleCancel }}
                    confirm={{ label: t('common.save'), type: 'submit' }}
                />
            }
        >
            <Grid container spacing={2} justifyContent='center' id='create-product'>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            id='title'
                            label={t('products.fields.title')}
                            value={metafields.name.value}
                            onChange={e => {
                                setMetafields(prevFields => ({
                                    ...prevFields,
                                    name: {
                                        ...prevFields.name,
                                        value: capitalizeFirstLetters(e.target.value)
                                    }
                                }))
                            }}
                        ></TextField>
                    </Grid> 
                    <Grid item xs={12} sm={6}>
                        <Autocomplete
                            blurOnSelect
                            id='category'
                            value={fields.category || null}
                            options={categories}
                            onChange={(e, value) => handleChange(e, value)}
                            renderInput={params => (
                                <TextField
                                    {...params}
                                    label={t('products.fields.category')}
                                    value={fields.category}
                                    required
                                />
                            )}
                        />
                    </Grid>
                    {categoryIsRing && (
                        <Grid item xs={12} sm={6}>
                            <TextField
                                autoComplete='off'
                                fullWidth
                                type='number'
                                id='ring_size'
                                label={t('products.fields.ringSize')}
                                value={metafields.ring_size.value}
                                onChange={e => handleMetafieldChange(e)}
                            ></TextField>
                        </Grid>
                    )}

                    {categoryNeedsLength && (
                        <Grid item xs={12} sm={6}>
                            <TextField
                                autoComplete='off'
                                InputProps={{
                                    endAdornment: <InputAdornment position='end'>in.</InputAdornment>
                                }}
                                fullWidth
                                type='number'
                                id='length'
                                label={t('products.fields.length')}
                                value={metafields.length.value.value}
                                onChange={e => handleMetafieldChange(e)}
                            ></TextField>
                        </Grid>
                    )}
                    {!categoryIsWatch && (
                        <Grid item xs={12} sm={6}>
                            <Autocomplete
                                value={metafields.metal.value || null}
                                blurOnSelect
                                id='metal'
                                options={metalsList}
                                onChange={(e, value) => handleMetafieldChange(e, value)}
                                renderInput={params => (
                                    <TextField
                                        {...params}
                                        label={t('products.fields.metal')}
                                        // value={metafields.metal}
                                        required={!categoryIsWatch}
                                    />
                                )}
                            />
                        </Grid>
                    )}
                    {categoryIsWatch && (
                        <Grid item xs={12} sm={6}>
                            <TextField
                                required={categoryIsWatch}
                                autoComplete='off'
                                fullWidth
                                id='watch_model'
                                label={t('products.fields.modelNumber')}
                                value={metafields.watch_model.value}
                                onChange={e => handleMetafieldChange(e)}
                            ></TextField>
                        </Grid>
                    )}

                    {/* <Grid item xs={12} sm={6}> */}
                    <Grid item xs={12} sm={categoryNeedsLength || categoryIsRing ? 12 : 6}>
                        <FormControl sx={{ width: '100%' }}>
                            <InputLabel id='color-label'>{t('products.fields.color')}</InputLabel>
                            <Select
                                labelId='color-label'
                                id='color'
                                fullWidth
                                required={
                                    !categoryIsWatch &&
                                    !['Silver', 'Tungsten', 'Stainless Steel'].includes(
                                        metafields.metal.value
                                    )
                                }
                                multiple
                                value={
                                    typeof metafields.color.value === 'object'
                                        ? metafields.color.value
                                        : typeof metafields.color.value === 'string'
                                        ? JSON.parse(metafields.color.value)
                                        : []
                                }
                                onChange={e => {
                                    setMetafields(prevFields => ({
                                        ...prevFields,
                                        color: {
                                            ...prevFields.color,
                                            value:
                                                typeof e.target.value === 'string'
                                                    ? colors.split(',')
                                                    : e.target.value
                                        }
                                    }))
                                }}
                                input={<OutlinedInput label={t('products.fields.color')} />}
                                renderValue={selected => selected.join(', ')}
                            >
                                {colorList.map(color => (
                                    <MenuItem key={color} value={color}>
                                        <Checkbox checked={metafields.color.value.includes(color)} />
                                        <ListItemText primary={color} />
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField
                            autoComplete='off'
                            InputProps={{
                                startAdornment: <InputAdornment position='start'>$</InputAdornment>
                            }}
                            fullWidth
                            type='number'
                            id='cost'
                            label={t('products.fields.cost')}
                            value={fields.cost}
                            onChange={e => handleChange(e)}
                        ></TextField>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField
                            required
                            autoComplete='off'
                            InputProps={{
                                startAdornment: <InputAdornment position='start'>$</InputAdornment>
                            }}
                            fullWidth
                            type='number'
                            id='price'
                            label={t('products.fields.salePrice')}
                            value={fields.price}
                            onChange={e => handleChange(e)}
                        ></TextField>
                    </Grid>

                    <Grid item xs={11} sm={5}>
                        <TextField
                            autoComplete='off'
                            placeholder='0'
                            id='nwk_quantity'
                            label={t('products.fields.newarkQuantity')}
                            type='number'
                            value={fields.nwk_quantity}
                            onChange={e => handleChange(e)}
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                            // align='left'
                        />
                    </Grid>
                    <Grid
                        container
                        item
                        xs={1}
                        alignItems='center'
                        alignContent='space-between'
                        justifyContent='flex-start'
                        pl={{ xs: '6px !important', sm: '15px !important' }}
                        sx={{ paddingLeft: '15px !important' }}
                    >
                        <Grid item>
                            <IconButton
                                size='small'
                                sx={{ padding: 0, transform: 'rotate(180deg)', color: 'text.secondary' }}
                                onClick={() => {
                                    setFields(prevFields => ({
                                        ...prevFields,
                                        nwk_quantity: prevFields.nwk_quantity + 1
                                    }))
                                }}
                            >
                                <ArrowDropDownCircleIcon />
                            </IconButton>
                        </Grid>
                        <Grid item>
                            <IconButton
                                size='small'
                                sx={{ padding: 0, color: 'text.secondary' }}
                                onClick={() => {
                                    setFields(prevFields => ({
                                        ...prevFields,
                                        nwk_quantity: prevFields.nwk_quantity - 1
                                    }))
                                }}
                            >
                                <ArrowDropDownCircleIcon />
                            </IconButton>
                        </Grid>
                    </Grid>
                    <Grid item xs={11} sm={5}>
                        <TextField
                            autoComplete='off'
                            placeholder='0'
                            id='lb_quantity'
                            label={t('products.fields.longBranchQuantity')}
                            type='number'
                            value={fields.lb_quantity}
                            onChange={e => handleChange(e)}
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                            // align='right'
                        />
                    </Grid>
                    <Grid
                        container
                        item
                        xs={1}
                        alignItems='center'
                        alignContent='space-between'
                        justifyContent='flex-start'
                        pl={{ xs: '6px !important', sm: '15px !important' }}
                        sx={{ paddingLeft: '15px !important' }}
                    >
                        <Grid item>
                            <IconButton
                                size='small'
                                sx={{ padding: 0, transform: 'rotate(180deg)', color: 'text.secondary' }}
                                onClick={() => {
                                    setFields(prevFields => ({
                                        ...prevFields,
                                        lb_quantity: prevFields.lb_quantity + 1
                                    }))
                                }}
                            >
                                <ArrowDropDownCircleIcon />
                            </IconButton>
                        </Grid>
                        <Grid item>
                            <IconButton
                                size='small'
                                sx={{ padding: 0, color: 'text.secondary' }}
                                onClick={() => {
                                    setFields(prevFields => ({
                                        ...prevFields,
                                        lb_quantity: prevFields.lb_quantity - 1
                                    }))
                                }}
                            >
                                <ArrowDropDownCircleIcon />
                            </IconButton>
                        </Grid>
                    </Grid>
                    {categoryIsWeddingBand && (
                        <Grid item xs={12} sm={6} align='right'>
                            <TextField
                                placeholder='0'
                                InputProps={{
                                    endAdornment: <InputAdornment position='end'>mm</InputAdornment>
                                }}
                                required={categoryIsWeddingBand}
                                fullWidth
                                type='number'
                                inputProps={{ step: '0.01' }}
                                id='band_width'
                                label={t('products.fields.bandWidth')}
                                value={metafields.band_width.value}
                                onChange={e => handleMetafieldChange(e)}
                            ></TextField>
                        </Grid>
                    )}
                    {!categoryIsWatch && (
                        <Grid item xs={12}>
                            <Autocomplete
                                multiple
                                id='gemstones'
                                value={metafields.gemstones.value}
                                blurOnSelect
                                onChange={(_e, value) =>
                                    setMetafields(prevFields => ({
                                        ...prevFields,
                                        gemstones: { ...prevFields.gemstones, value: value }
                                    }))
                                }
                                options={gemstonesList}
                                filterSelectedOptions
                                renderInput={params => <TextField {...params} label={t('products.fields.gemstones')} />}
                            />
                        </Grid>
                    )}
                    {hasStoneSizes && (
                        <>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    placeholder='0.00'
                                    InputProps={{
                                        endAdornment: <InputAdornment position='end'>ct</InputAdornment>
                                    }}
                                    fullWidth
                                    type='number'
                                    inputProps={{ step: '0.01' }}
                                    id='center_stone'
                                    label={t('products.fields.centerStone')}
                                    value={metafields.center_stone.value}
                                    onChange={e => handleMetafieldChange(e)}
                                ></TextField>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    placeholder='0.00'
                                    InputProps={{
                                        endAdornment: <InputAdornment position='end'>ct</InputAdornment>
                                    }}
                                    fullWidth
                                    type='number'
                                    inputProps={{ step: '0.01' }}
                                    id='side_stones'
                                    label={t('products.fields.sideStones')}
                                    value={metafields.side_stones.value}
                                    onChange={e => handleMetafieldChange(e)}
                                ></TextField>
                            </Grid>
                        </>
                    )}
                    {hasDescription && (
                        <Grid item xs={12}>
                            <TextField
                                autoComplete='off'
                                fullWidth
                                multiline
                                id='body_html'
                                label={t('products.fields.description')}
                                value={fields.body_html}
                                onChange={e => handleChange(e)}
                            ></TextField>
                        </Grid>
                    )}

                    <Grid item xs={12} sm={6}>
                        <Autocomplete
                            blurOnSelect
                            id='vendor'
                            value={fields.vendor || null}
                            options={vendorOptions}
                            onChange={(e, value) => handleChange(e, value)}
                            renderInput={params => (
                                <TextField {...params} label={t('products.fields.vendor')} value={fields.vendor} required />
                            )}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField
                            required
                            autoComplete='off'
                            InputProps={{
                                endAdornment: <InputAdornment position='end'>g</InputAdornment>
                            }}
                            fullWidth
                            type='number'
                            id='weight'
                            label={t('products.fields.weight')}
                            value={fields.weight}
                            onChange={e => handleChange(e)}
                        ></TextField>
                    </Grid>

                    <Grid item container direction='row' xs={12} justifyContent='center'>
                        <FormGroup sx={{ flexDirection: 'row' }}>
                            <Grid item xs={6}>
                                <FormControlLabel
                                    id='has-stone-size'
                                    disabled={metafields.gemstones.value.length < 1}
                                    control={<Checkbox />}
                                    label={t('products.fields.stoneSize')}
                                    checked={hasStoneSizes}
                                    onChange={e => {
                                        setHasStoneSizes(e.target.checked)
                                    }}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <FormControlLabel
                                    id='has-description'
                                    control={<Checkbox />}
                                    label={t('products.fields.description')}
                                    checked={hasDescription}
                                    onChange={e => {
                                        setHasDescription(e.target.checked)
                                    }}
                                />
                            </Grid>
                        </FormGroup>
                    </Grid>

                    {edit && (
                        <Grid item xs={12} id='create-product-image'>
                            <img
                                src={fields.image}
                                style={{ maxWidth: 'min(388px, 90%)' }}
                                alt='product'
                            />
                        </Grid>
                    )}                

                    {!edit && (
                        <Grid item xs={12} align='center'>
                            <Box
                                component='div'
                                sx={{ position: 'relative' }}
                            >
                                {!imageURLs.length && (
                                    <label htmlFor='uploadImage'>
                                        <Input
                                            accept='image/*'
                                            id='uploadImage'
                                            multiple
                                            type='file'
                                            onChange={handleImageSelect}
                                        />
                                        {/* A drop zone, not a button. It was a filled
                                            #919191 slab with a 25px radius -- the
                                            loudest thing on the form after Save, for
                                            an optional field, in a grey that belonged
                                            to no palette. */}
                                        <Button
                                            component='span'
                                            fullWidth
                                            sx={{
                                                minHeight: 160,
                                                flexDirection: 'column',
                                                gap: 1,
                                                color: 'text.secondary',
                                                borderStyle: 'dashed',
                                                borderWidth: 1,
                                                borderColor: 'divider',
                                                bgcolor: 'background.paper',
                                                '&:hover': {
                                                    borderStyle: 'dashed',
                                                    borderColor: 'primary.main',
                                                    bgcolor: 'action.hover'
                                                }
                                            }}
                                            variant='outlined'
                                        >
                                            <CameraAltIcon sx={{ fontSize: 40 }} />
                                            <Typography variant='body2'>{t('products.fields.addPhoto')}</Typography>
                                        </Button>
                                    </label>
                                )}
                                {imageURLs.length > 0 && (
                                    <>
                                        <img
                                            src={imageURLs[0]}
                                            style={{ maxHeight: '234px' }}
                                            alt='product'
                                        />
                                        <DeleteIcon
                                            sx={{
                                                position: 'absolute',
                                                color: 'text.secondary',
                                                padding: '11px',
                                                fontSize: '1.5em',
                                                top: 0,
                                                right: 0
                                            }}
                                            onClick={() => setShowDeleteConfirmation(true)}
                                        />
                                    </>
                                )}
                                {showDeleteConfirmation && (
                                    <Sheet
                                        open={showDeleteConfirmation}
                                        onClose={() => setShowDeleteConfirmation(false)}
                                        title={t('products.confirmDeleteImage')}
                                        actions={
                                            /* Confirm here deletes the photo, so it
                                               is the red one, not the green one. */
                                            <ActionBar
                                                cancel={{ label: t('common.cancel'), onClick: () => setShowDeleteConfirmation(false) }}
                                                confirm={{ label: t('common.delete'), onClick: handleImageDelete, tone: 'danger' }}
                                            />
                                        }
                                    />
                                )}
                            </Box>
                        </Grid>
                    )}
            </Grid>
        </FormPage>
    )
}
